// =====================================================================
// src/screens/v2/HomeConnectListV2.jsx
//
// "From your Connect List" horizontal scroller rendered inside V1's
// Home → Wall sub-tab for V2-enabled users (Step 6.6d).
//
// Sits between the composer row and the holidays/events/posts feed.
//
// Three states:
//   1. Loading           — render nothing (avoids layout flash)
//   2. Empty             — small nudge: "Start connecting"
//   3. Has connections   — title bar + horizontal scroller with cards
//
// Title bar shows "FROM YOUR CONNECT LIST · N" + [See all →] + collapse
// chevron. Collapse state lives in component memory only (resets per
// mount, which is fine — collapsing is a per-session preference, not
// a persistent setting).
//
// Cards: gradient background per category, emoji logo, biz name + sub.
// Tap card  → ?v2=biz-profile&id=<businessId>
// Tap See all → ?v2=connect-list
//
// Field-shape defensive: tries conn.businessName || conn.name || ...,
// since the connection doc's exact schema isn't centrally documented.
// If a field comes back blank, the card still renders without crashing.
// =====================================================================

import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, Bookmark } from 'lucide-react';
import { getUserConnections, getBusiness, logoBgFromCategory } from '../../lib/v2/firestore';
// Phase 2.5 / C10d — surface latest post per tile
import { getLatestWallPost, POST_TYPES } from '../../lib/v2/wallPosts';
import './HomeConnectListV2.css';

// Category → emoji for business logo (shared pattern with ProfileTabV2,
// OrderChat, Cart, OrderDetail, MerchantApp). Inline to keep V2 screens
// loosely coupled.
// U8: this map previously used keys (apparel, services, home_decor) that did
// NOT match the category values real business docs carry (fashion_apparel,
// home_services, handmade, ...). Every non-food business fell through to the
// generic fallback with an EMPTY label, so only Food & Beverage rendered a
// proper category. Keys now cover both the canonical CATEGORY_OPTIONS ids
// (MerchantApp) AND the real-world values seen in production data, including
// the food aliases from FOOD_CATEGORIES (dairy/bakery/handmade).
// (Longer-term this map is duplicated across five files and should live in one
//  shared module — flagged, not done here.)
const CATEGORY_EMOJI = {
  food_beverages: '🍯',
  dairy: '🥛',
  bakery: '🍰',
  handmade: '🎨',
  apparel: '👗',
  fashion_apparel: '👗',
  services: '🔧',
  home_services: '🔧',
  groceries: '🛒',
  grocery: '🛒',
  electronics: '📱',
  health_beauty: '💄',
  home_decor: '🏡',
  home_deco: '🏡',
  other: '🏪',
};
function emojiForCategory(cat) {
  return CATEGORY_EMOJI[cat] || '🏪';
}

// Pretty label for the category subtitle on each card.
const CATEGORY_LABEL = {
  food_beverages: 'Food & Beverages',
  dairy: 'Dairy',
  bakery: 'Bakery',
  handmade: 'Handmade',
  apparel: 'Fashion & Apparel',
  fashion_apparel: 'Fashion & Apparel',
  services: 'Services',
  home_services: 'Home Services',
  groceries: 'Groceries',
  grocery: 'Groceries',
  electronics: 'Electronics',
  health_beauty: 'Health & Beauty',
  home_decor: 'Home & Décor',
  home_deco: 'Home & Décor',
  other: 'Local business',
};
function labelForCategory(cat) {
  // U8: never blank — an unmapped category now reads 'Local business'
  // rather than an empty subtitle.
  return CATEGORY_LABEL[cat] || 'Local business';
}

export default function HomeConnectListV2({ currentUser }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const uid = currentUser?.id || currentUser?.uid;
    if (!uid) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        // The /connections doc is minimal — { userId, businessId, status,
        // createdAt }. We need the business doc for name/category/logo.
        // Pattern mirrors ConnectList.jsx exactly: fetch all conns, then
        // fetch each referenced business in parallel, filter missing.
        const conns = await getUserConnections();
        if (cancelled) return;
        if (!Array.isArray(conns) || conns.length === 0) {
          setConnections([]);
          return;
        }
        const bizDocs = await Promise.all(
          conns.map(c => getBusiness(c.businessId).catch(() => null))
        );
        if (cancelled) return;
        const merged = conns
          .map((c, i) => {
            const biz = bizDocs[i];
            if (!biz) return null;   // business deleted or unreadable
            return {
              id: biz.id || c.businessId,
              name: biz.name || 'Business',
              category: biz.category,
              logo: biz.logo || null,        // business's own emoji override
              // Phase 2.5 / C10d — surface uploaded logo on home tile
              // (was already used on biz profile + merchant top bar)
              logoImageUrl: biz.logoImageUrl || null,
              verified: !!biz.verified,
              // Carry the status enum too: the ✓ tick must require BOTH the
              // boolean AND status === 'verified'. Approve historically wrote
              // only the boolean, so some docs have verified:true with
              // status:'pending_verification' (the drift fixed in
              // approveBusiness). The full-screen ConnectList already guards
              // the tick with both (Y9); the home strip trusted the boolean
              // alone and showed ticks for not-actually-verified businesses.
              businessStatus: biz.status || null,
              shortDescription: biz.shortDescription || '',
              // latestPost filled in below after a parallel fetch
              latestPost: null,
            };
          })
          .filter(Boolean);
        // Phase 2.5 / C10d — for each business, fetch the most recent
        // wall post in parallel. One-shot reads (not subscriptions) —
        // home reload on next navigation refreshes them, and a real-time
        // listener per connected business would be wasteful at scale.
        // Failed fetches resolve to null and the tile renders without a
        // teaser pill (graceful degradation).
        const latestPosts = await Promise.all(
          merged.map(m => getLatestWallPost(m.id))
        );
        if (cancelled) return;
        const withPosts = merged.map((m, i) => ({ ...m, latestPost: latestPosts[i] }));
        setConnections(withPosts);
      } catch (err) {
        // Non-fatal — connect list just stays empty.
        console.warn('[HomeConnectListV2] load failed:', err?.message);
        if (!cancelled) setConnections([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id, currentUser?.uid]);

  function handleOpenBusiness(businessId) {
    if (!businessId) return;
    window.location.search = '?v2=biz-profile&id=' + businessId;
  }

  function handleSeeAll() {
    window.location.search = '?v2=connect-list';
  }

  function handleToggle(e) {
    e.stopPropagation();
    setCollapsed(prev => !prev);
  }

  // ---- Loading: render nothing to avoid flash ----
  if (loading) return null;

  // ---- Empty: tiny nudge, no big banner ----
  if (connections.length === 0) {
    return (
      <div className="v2hcl-wrap v2hcl-empty">
        <div className="v2hcl-empty-card">
          <div className="v2hcl-empty-icon"><Bookmark size={16} /></div>
          <div className="v2hcl-empty-text">
            <div className="v2hcl-empty-title">Start connecting</div>
            <div className="v2hcl-empty-sub">with businesses you love</div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Has connections ----
  return (
    <div className="v2hcl-wrap">
      <div className="v2hcl-title-bar">
        <span className="v2hcl-title">
          FROM YOUR CONNECT LIST · <span className="v2hcl-count">{connections.length}</span>
        </span>
        <button className="v2hcl-see-all" onClick={handleSeeAll}>
          See all →
        </button>
        <button
          className="v2hcl-toggle"
          onClick={handleToggle}
          aria-label={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        </button>
      </div>
      {!collapsed && (
        <div className="v2hcl-scroller">
          {connections.map((conn) => {
            // Phase 2.5 / C10d — derive teaser pill from latest post.
            // Pill only renders for marketing-intent post types (offer,
            // new_product, announcement). 'General' posts aren't surfaced
            // on the home strip — they're often chatty/internal updates
            // not worth competing for tile real estate.
            const eligibleTypes = new Set(['offer', 'new_product', 'announcement']);
            const showTeaser = conn.latestPost && eligibleTypes.has(conn.latestPost.type);
            const postTypeDef = showTeaser
              ? POST_TYPES.find(t => t.id === conn.latestPost.type)
              : null;
            const teaserText = conn.latestPost?.text?.trim() || '';
            const teaserPill = postTypeDef && teaserText
              ? `${postTypeDef.emoji} ${teaserText.length > 30 ? teaserText.slice(0, 30).trim() + '…' : teaserText}`
              : null;
            return (
              <button
                key={conn.id}
                className="v2hcl-card"
                onClick={() => handleOpenBusiness(conn.id)}
              >
                <div
                  className="v2hcl-card-logo-bg"
                  style={{
                    background: conn.logoImageUrl ? 'transparent' : logoBgFromCategory(conn.category),
                  }}
                >
                  {conn.verified && conn.businessStatus === 'verified' && (
                    <div className="v2hcl-card-verified" aria-label="Verified">✓</div>
                  )}
                  {conn.logoImageUrl ? (
                    <img
                      src={conn.logoImageUrl}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <span className="v2hcl-card-emoji">
                      {conn.logo || emojiForCategory(conn.category)}
                    </span>
                  )}
                </div>
                <div className="v2hcl-card-body">
                  <div className="v2hcl-card-name">{conn.name}</div>
                  {/* Category always shown (was being replaced by teaser
                      pill in the first cut). When the business has a
                      marketing-intent post, the pill renders BELOW the
                      category label — prototype parity. */}
                  <div className="v2hcl-card-sub">{labelForCategory(conn.category)}</div>
                  {teaserPill && (
                    <div
                      style={{
                        marginTop: 4,
                        display: 'inline-block',
                        background: 'var(--accent-soft)',
                        color: 'var(--accent)',
                        padding: '3px 8px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 500,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={teaserText}
                    >
                      {teaserPill}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
